import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import Pagination from '@/components/base/Pagination';
import { catalog } from '@/mocks/catalog';
import usePageMeta from '@/hooks/usePageMeta';
import FilterControls, { type FilterState } from './components/FilterControls';

const PAGE_SIZE = 8;

const TYPE_CONFIG = {
  movie: {
    name: '电影',
    title: '电影频道',
    desc: '汇聚院线新片、经典佳作与类型电影，覆盖科幻、动作、悬疑、剧情等多种题材。',
  },
  tv: {
    name: '电视剧',
    title: '电视剧频道',
    desc: '热播剧集、口碑长剧与单元剧集，涵盖古装、都市、犯罪、青春等丰富类型。',
  },
} as const;

type CategoryType = keyof typeof TYPE_CONFIG;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isValidType = type === 'movie' || type === 'tv';
  const config = isValidType ? TYPE_CONFIG[type as CategoryType] : null;

  const filters: FilterState = {
    genre: searchParams.get('genre') || '全部',
    region: searchParams.get('region') || '全部',
    year: searchParams.get('year') || '全部',
    sort: searchParams.get('sort') || 'latest',
  };
  const page = Math.max(1, Number(searchParams.get('page')) || 1);

  usePageMeta(
    config ? `${config.name} - 快活影院` : '分类 - 快活影院',
    config
      ? `${config.desc}快活影院为您整理最新${config.name}资源与详细资料。`
      : '快活影院分类页面。',
  );

  const filtered = useMemo(() => {
    if (!config) return [];
    return catalog
      .filter((item) => item.type === type)
      .filter((item) => filters.genre === '全部' || item.genres.includes(filters.genre))
      .filter((item) => filters.region === '全部' || item.region === filters.region)
      .filter((item) => filters.year === '全部' || String(item.year) === filters.year)
      .sort((a, b) => {
        if (filters.sort === 'rating') return b.rating - a.rating;
        if (filters.sort === 'hot') return b.views - a.views;
        return b.year - a.year;
      });
  }, [config, type, filters.genre, filters.region, filters.year, filters.sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setDrawerOpen(false);
  }, [searchParams]);

  const updateFilters = (patch: Partial<FilterState>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value && value !== '全部' && !(key === 'sort' && value === 'latest')) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    });
    next.delete('page');
    setSearchParams(next);
  };

  const resetFilters = () => setSearchParams(new URLSearchParams());
  const goPage = (next: number) => {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete('page');
    else params.set('page', String(next));
    setSearchParams(params);
  };

  if (!config) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-24 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading font-bold text-xl text-foreground-950">分类不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">请选择电影或电视剧频道进行浏览。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full pb-4">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        {/* breadcrumb */}
        <nav aria-label="面包屑" className="pt-6 text-sm text-foreground-500">
          <ol className="flex items-center gap-1.5">
            <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li className="text-foreground-900">{config.name}</li>
          </ol>
        </nav>

        <header className="mt-4">
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
            {config.title}
            <span className="ml-3 align-middle text-base font-normal text-foreground-400">
              {config.name} · 共 {filtered.length} 部
            </span>
          </h1>
          <p className="mt-2 text-sm text-foreground-500 max-w-3xl">{config.desc}</p>
        </header>

        {/* desktop filter panel */}
        <div className="hidden lg:block mt-6 rounded-lg border border-background-200 bg-background-50 px-5 py-2">
          <FilterControls filters={filters} onChange={updateFilters} />
        </div>

        {/* mobile filter trigger */}
        <div className="lg:hidden mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap"
          >
            <i className="ri-filter-3-line"></i>
            筛选与排序
          </button>
          <span className="text-sm text-foreground-500">
            共 {filtered.length} 部 · 第 {currentPage}/{totalPages} 页
          </span>
        </div>

        {/* list */}
        {pageItems.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {pageItems.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="mt-10 py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-100">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
              <i className="ri-inbox-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading font-semibold text-lg text-foreground-900">
              抱歉，暂无符合条件的影视作品
            </h2>
            <p className="mt-2 text-sm text-foreground-500">试着调整或重置筛选条件，看看更多内容。</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}

        <Pagination page={currentPage} totalPages={totalPages} onChange={goPage} />
      </div>

      {/* mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-background-950/50"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[82vh] rounded-t-lg bg-background-50 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-background-200">
              <h2 className="font-heading font-bold text-base text-foreground-950">筛选与排序</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="px-4 py-2 overflow-y-auto">
              <FilterControls filters={filters} onChange={updateFilters} />
            </div>
            <div className="p-4 border-t border-background-200 flex gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm"
              >
                查看 {filtered.length} 部结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}